import { z } from 'zod';

import { memberLevelSchema, socialNetworkSchema } from './members';

/**
 * Carta de membro (estilo carta FIFA): a foto de perfil, o nickname (o login,
 * nunca o nome verdadeiro), o nivel e tres numeros da vida na associacao.
 *
 * So ve a foto quem pode ler o perfil (o proprio, o encarregado, os
 * administradores). Para os outros a carta vem com `photo: false` e mostra uma
 * silhueta.
 */

/** Lado do quadrado guardado, em pixeis. */
export const MEMBER_PHOTO_SIZE = 512;
/** Maior ficheiro aceite no upload (o site ja manda o recorte em 512 px). */
export const MEMBER_PHOTO_MAX_BYTES = 5 * 1024 * 1024;
export const MEMBER_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

export const memberCardStatsSchema = z.object({
  /** Horas na Arena (sessoes terminadas). `null` se a Arena nao respondeu. */
  arenaHours: z.number().int().nonnegative().nullable(),
  /** Eventos em que fez check-in. `null` se o site nao respondeu. */
  events: z.number().int().nonnegative().nullable(),
  /** Anos completos como membro. `null` para quem ainda e so utilizador. */
  years: z.number().int().nonnegative().nullable(),
});
export type MemberCardStats = z.infer<typeof memberCardStatsSchema>;

/** `GET /me/card`, `GET /guardian/youths/:id/card`, `GET /members/:id/card`. */
/**
 * Um premio do Hall of Fame Grow uP (uP Awards), que a carta mostra como uma
 * medalha. Os premios sao publicos. `icon` e um dos desenhos de
 * `HOF_ICONS` (@growup/site-shared); um desconhecido desenha-se como medalha.
 */
export const cardAwardSchema = z.object({
  category: z.string(),
  icon: z.string(),
  edition: z.string(),
});
export type CardAward = z.infer<typeof cardAwardSchema>;

export const memberCardSchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
  level: memberLevelSchema,
  memberNumber: z.number().int().positive().nullable(),
  memberSince: z.string().datetime().nullable(),
  /** Ha foto e quem pede pode ve-la. */
  photo: z.boolean(),
  /** Muda quando a foto muda: serve para nao mostrar a antiga da cache. */
  photoVersion: z.string().nullable(),
  stats: memberCardStatsSchema,
  /** Do mais recente para o mais antigo. Vazio se o site nao respondeu. */
  awards: z.array(cardAwardSchema).default([]),
  /** Streamer ativo no Stream Portal: a carta fica roxa. `false` se a streaming-api nao respondeu. */
  streamer: z.boolean().default(false),
});
export type MemberCard = z.infer<typeof memberCardSchema>;

/**
 * `GET /members/cards`: a grelha de cartas do back-office, por antiguidade
 * (`memberSince` mais antigo primeiro; sem data, no fim). So os dados que nao
 * obrigam a perguntar a Arena: as horas e os eventos ficam na ficha. A foto
 * segue a mesma regra da carta.
 */
export const memberCardSummarySchema = memberCardSchema
  .pick({
    accountId: true,
    login: true,
    level: true,
    memberNumber: true,
    memberSince: true,
    photo: true,
    photoVersion: true,
    awards: true,
    streamer: true,
  })
  .extend({
    /** Anos completos como membro, como em `stats.years`. */
    years: z.number().int().nonnegative().nullable(),
  });
export type MemberCardSummary = z.infer<typeof memberCardSummarySchema>;

/** `GET /internal/accounts/:id/card-stats` da arena-api. */
export const arenaCardStatsSchema = z.object({ minutes: z.number().int().nonnegative() });
export type ArenaCardStats = z.infer<typeof arenaCardStatsSchema>;

/** `GET /internal/accounts/:id/card-stats` da site-api. */
export const siteCardStatsSchema = z.object({
  eventsAttended: z.number().int().nonnegative(),
  awards: z.array(cardAwardSchema).default([]),
});
export type SiteCardStats = z.infer<typeof siteCardStatsSchema>;

/**
 * `POST /internal/card-awards` da site-api: as medalhas de varias contas num so
 * pedido (a grelha de cartas do back-office pede 48 de cada vez).
 */
export const CARD_AWARDS_MAX = 200;
export const cardAwardsRequestSchema = z.object({
  accountIds: z.array(z.string().uuid()).max(CARD_AWARDS_MAX),
});
export type CardAwardsRequest = z.infer<typeof cardAwardsRequestSchema>;
/** Por conta; uma conta sem premios pode faltar. */
export const cardAwardsBatchSchema = z.record(z.string(), z.array(cardAwardSchema));
export type CardAwardsBatch = z.infer<typeof cardAwardsBatchSchema>;

/**
 * Pagina publica de cada membro (F-009): `growup.gg/membros/<nickname>`. So
 * aparece quando a propria pessoa a ativa (opt-in). `GET/PUT /me/public-page`.
 */
export const publicPageSettingsSchema = z.object({
  enabled: z.boolean(),
  /** Mostrar a foto da carta na pagina (desligado por defeito). */
  showPhoto: z.boolean(),
  /**
   * Porque nao pode ter pagina (`null` = pode): `minor` e menor; `no_profile`
   * ainda nao deu a data de nascimento (so com o pedido de membro, que e
   * gratis), e sem ela nao se sabe se e maior.
   */
  unavailable: z.enum(['minor', 'no_profile']).nullable(),
  /** O admin escondeu a pagina (moderacao): nao aparece, mesmo ativa. */
  hiddenByStaff: z.boolean(),
  /** As redes que a pessoa escolheu mostrar (so das ligadas). */
  networks: z.array(socialNetworkSchema).default([]),
  /** As redes ligadas na conta, que se podem escolher. */
  linked: z.array(socialNetworkSchema).default([]),
});
export type PublicPageSettings = z.infer<typeof publicPageSettingsSchema>;

export const publicPageUpdateSchema = z.object({
  enabled: z.boolean(),
  showPhoto: z.boolean(),
  networks: z.array(socialNetworkSchema).max(5).default([]),
});
export type PublicPageUpdate = z.infer<typeof publicPageUpdateSchema>;

/**
 * `GET /internal/members/:id/public-page` (site-api). `visible`: a pessoa
 * ativou, nao e menor e nao foi escondida. A carta vem sempre sem numero de
 * socio nem data de entrada; com foto so se `visible` e a pessoa a escolheu.
 */
export const internalPublicPageSchema = z.object({
  visible: z.boolean(),
  card: memberCardSchema,
  /** As redes escolhidas, com o nome de utilizador; vazio se a pagina nao estiver visivel. */
  social: z.array(z.object({ network: socialNetworkSchema, handle: z.string() })).default([]),
});
export type InternalPublicPage = z.infer<typeof internalPublicPageSchema>;

/** `POST /internal/members/public-pages`: das contas dadas, as que tem a pagina visivel. */
export const internalPublicPagesResponseSchema = z.object({ visible: z.array(z.string().uuid()) });

/**
 * `GET /members/:id/public-page` no back-office: o estado da pagina publica e
 * se o admin a escondeu (moderacao). `PUT .../hidden` esconde ou volta a mostrar.
 */
export const adminPublicPageSchema = z.object({
  enabled: z.boolean(),
  showPhoto: z.boolean(),
  hiddenAt: z.string().datetime().nullable(),
  hiddenReason: z.string().nullable(),
});
export type AdminPublicPage = z.infer<typeof adminPublicPageSchema>;

export const publicPageHideRequestSchema = z.discriminatedUnion('hidden', [
  z.object({ hidden: z.literal(true), reason: z.string().trim().min(3).max(300) }),
  z.object({ hidden: z.literal(false) }),
]);
export type PublicPageHideRequest = z.infer<typeof publicPageHideRequestSchema>;

/**
 * `GET /internal/members/public-wall` (site-api): todas as pessoas com a pagina
 * publica visivel, por antiguidade (`member_since` mais antigo primeiro; sem
 * data, no fim). A foto so de quem a escolheu mostrar.
 */
export const internalPublicWallSchema = z.object({
  members: z.array(
    z.object({
      accountId: z.string().uuid(),
      level: memberLevelSchema,
      years: z.number().int().nonnegative().nullable(),
      streamer: z.boolean(),
      photoVersion: z.string().nullable(),
    }),
  ),
});
export type InternalPublicWall = z.infer<typeof internalPublicWallSchema>;
